import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useMediaQuery,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { AddLetterForm } from "./add-letter-form";
import { AddLetter, GetLetterById, EditLetter, ListLetterHead } from "../../services/letter";
import { trackFormSubmit, trackEvent } from "../../utils/analytics";

export default function AddLetterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location.state?.mode === "edit";
  const letterId = location.state?.letterId;
  const currentUserId = useSelector((state) => state.user.iduser);
  const isMobile = useMediaQuery("(max-width:960px)"); // Mobile breakpoint

  const [formData, setFormData] = useState({
    dateletter: new Date().toISOString(), // Default to current date in ISO format
    numletter: "",
    isattach: false, // For label display checkbox (from adjustment page)
    isAttachment: false, // Whether the letter has attachment or not
    attachmentNum: "",
    sender: "",
    receiver: "",
    title: "",
    textletter: "",
    fullname: "",
    postName: "",
    isroonevesht: false,
    roonevesht: "",
    isdate: false,
    isnumletter: false,
    lang: "fa",
    file: null,
    idemza: 0,
    idemzastr: "",
    idsarbarg: 0,
    idsarbargstr: "",
    idletter: 0,
    idletterstr: "",
    xdate: 0,
    ydate: 0,
    xatach: 0,
    yatach: 0,
    xnumletter: 0,
    ynumletter: 0,
    showDateLabel: false,
    showNumLabel: false,
    showAttachLabel: false,
    useTemplate: true,
    useSignature: true,
  });

  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [emzaDrawerOpen, setEmzaDrawerOpen] = useState(false);
  const [hasLetterSettings, setHasLetterSettings] = useState(false);
  const [isCheckingLetterHead, setIsCheckingLetterHead] = useState(true);
  const [showMobileMessage, setShowMobileMessage] = useState(false);

  // Check for letter head configuration on mount (always check, regardless of screen size)
  useEffect(() => {
    const checkLetterHead = async () => {
      // Skip check in edit mode or if coming from adjustment page
      if (isEditMode || location.state?.formData) {
        setIsCheckingLetterHead(false);
        return;
      }

      try {
        setIsCheckingLetterHead(true);
        const response = await ListLetterHead();
        
        // Handle different response structures
        const letterHeadList = Array.isArray(response.data) 
          ? response.data 
          : Array.isArray(response) 
          ? response 
          : [];
        
      
        
        // If no letter heads exist
        if (letterHeadList.length === 0) {
          console.log("No letter heads found");
          if (isMobile) {
            // Show message on mobile
            console.log("Mobile device - showing message");
            setShowMobileMessage(true);
          } else {
            // Navigate to adjustment page on desktop
            console.log("Desktop device - navigating to adjustment");
            navigate("/adjustment", {
              state: { formData, templateFile: formData.file || null },
            });
          }
        } else {
          console.log("Letter heads found, allowing form to show");
          // Letter heads exist, allow form to show normally (on any device)
          setShowMobileMessage(false);
        }
      } catch (error) {
        console.error("Error checking letter head:", error);
        // On error, allow form to show (don't block user)
        setShowMobileMessage(false);
      } finally {
        setIsCheckingLetterHead(false);
      }
    };

    checkLetterHead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Handle returning from adjustment page
  useEffect(() => {
    if (location.state?.formData) {
      const returnedData = location.state.formData;
      const returnedTemplate = location.state.templateFile;

      const hasConfiguredSettings =
        (returnedData.xdate !== 0 || returnedData.ydate !== 0) &&
        (returnedData.xnumletter !== 0 || returnedData.ynumletter !== 0);

      setHasLetterSettings(hasConfiguredSettings);

      setFormData((prev) => ({
        ...returnedData,
        file: returnedTemplate || prev.file, // Preserve template file
      }));
    }
  }, [location.state]);

  // Fetch letter data when in edit mode
  useEffect(() => {
    const fetchLetterData = async () => {
      if (isEditMode && letterId) {
        try {
          setIsLoadingData(true);
          const response = await GetLetterById(letterId);
          const letter = response.data;

          // Map API response to form data
          // Handle both capitalized and lowercase field names from API
          setFormData({
            dateletter: letter.dateletter || letter.Dateletter || new Date().toISOString(),
            numletter: letter.numletter || letter.Numletter || "",
            isattach: letter.isattach !== undefined ? letter.isattach : (letter.Isattach !== undefined ? letter.Isattach : true), // Label display
            isAttachment: letter.isAttachment || letter.IsAttachment || letter.isattach || letter.Isattach || false, // Attachment status
            attachmentNum: letter.attachmentNum || letter.AttachmentNum || "",
            sender: letter.sender || letter.Sender || "",
            receiver: letter.receiver || letter.Receiver || "",
            title: letter.title || letter.Title || "",
            textletter: letter.textletter || letter.Textletter || "",
            fullname: letter.fullname || letter.Fullname || "",
            postName: letter.postName || letter.PostName || "",
            isroonevesht: letter.isroonevesht || letter.Isroonevesht || false,
            roonevesht: letter.roonevesht || letter.Roonevesht || "",
            isdate: letter.isdate !== undefined ? letter.isdate : (letter.Isdate !== undefined ? letter.Isdate : true),
            isnumletter:
              letter.isnumletter !== undefined ? letter.isnumletter : (letter.Isnumletter !== undefined ? letter.Isnumletter : true),
            lang: letter.lang || "fa",
            file: null,
            idemza: letter.idemza || letter.Idemza || 0,
            idemzastr: letter.idemzastr || letter.Idemzastr || "",
            idsarbarg: letter.idsarbarg || letter.Idsarbarg || 0,
            idsarbargstr: letter.idsarbargstr || letter.Idsarbargstr || "",
            idletter: letter.idletter || letter.Idletter || 0,
            idletterstr: letter.idletterstr || letter.Idletterstr || letterId || "",
            // Preserve coordinate fields if they exist
            xdate: letter.xdate || letter.Xdate || 0,
            ydate: letter.ydate || letter.Ydate || 0,
            xatach: letter.xatach || letter.Xatach || 0,
            yatach: letter.yatach || letter.Yatach || 0,
            xnumletter: letter.xnumletter || letter.Xnumletter || 0,
            ynumletter: letter.ynumletter || letter.Ynumletter || 0,
            showDateLabel: letter.showDateLabel || false,
            showNumLabel: letter.showNumLabel || false,
            showAttachLabel: letter.showAttachLabel || false,
            useTemplate: letter.useTemplate !== undefined ? letter.useTemplate : true,
            useSignature: letter.useSignature !== undefined ? letter.useSignature : true,
          });
        } catch (error) {
          console.error("Error fetching letter data:", error);
          Swal.fire({
            title: "خطا",
            text: "مشکلی در دریافت اطلاعات نامه رخ داد",
            confirmButtonText: "باشه",
          }).then(() => {
            navigate("/letter");
          });
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchLetterData();
  }, [isEditMode, letterId, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (file) => {
    setFormData((prev) => ({ ...prev, file }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      // Debug: Log formData at the start
     

      // Validate required fields
      if (
        !formData.title ||
        !formData.textletter ||
        !formData.sender ||
        !formData.receiver
      ) {
        setMessage({
          type: "error",
          text: "لطفاً تمام فیلدهای ضروری را پر کنید",
        });
        setLoading(false);
        return;
      }

    
      // Helper to get number value
      const getNumberValue = (value) => {
        if (value === null || value === undefined) return 0;
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      };

      // Helper to get boolean value
      const getBooleanValue = (value) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
          return value === "true" || value === "1";
        }
        if (value === null || value === undefined) return false;
        return Boolean(value);
      };

      // Create JSON payload with all required fields
      const submitData = {};

      // Always include these required string fields - use formData directly
      submitData.title = formData.title ? String(formData.title) : "";
      submitData.Sender = formData.sender ? String(formData.sender) : "";
      submitData.Receiver = formData.receiver ? String(formData.receiver) : "";
      submitData.textletter = formData.textletter ? String(formData.textletter) : "";
      submitData.Fullname = formData.fullname ? String(formData.fullname) : "";
      submitData.postName = formData.postName ? String(formData.postName) : "";
      submitData.Numletter = formData.numletter ? String(formData.numletter) : "";
      submitData.roonevesht = formData.roonevesht ? String(formData.roonevesht) : "";
      submitData.dateletter = formData.dateletter ? String(formData.dateletter) : new Date().toISOString();
      submitData.lang = formData.lang ? String(formData.lang) : "fa";
      
      // Include string IDs - these are required fields
      // If useSignature is unchecked, set idemzastr to "0"
      submitData.idemzastr = (formData.useSignature === false) ? "0" : (formData.idemzastr ? String(formData.idemzastr) : "");
      // If useTemplate is unchecked, set idsarbargstr to "0"
      submitData.idsarbargstr = (formData.useTemplate === false) ? "0" : (formData.idsarbargstr ? String(formData.idsarbargstr) : "");
      
      // Include boolean fields
      submitData.isroonevesht = getBooleanValue(formData.isroonevesht);
      submitData.isAttachment = getBooleanValue(formData.isAttachment);
      submitData.isdate = getBooleanValue(formData.isdate);
      submitData.isattach = getBooleanValue(formData.isattach);
      submitData.isnumletter = getBooleanValue(formData.isnumletter);
      
      // Include numeric fields
      if (currentUserId) {
        submitData.idUser = getNumberValue(currentUserId);
      }
      if (formData.idemza) {
        submitData.idemza = getNumberValue(formData.idemza);
      }
      if (formData.idsarbarg) {
        submitData.idsarbarg = getNumberValue(formData.idsarbarg);
      }
      if (formData.xdate !== undefined && formData.xdate !== null) {
        submitData.xdate = getNumberValue(formData.xdate);
      }
      if (formData.ydate !== undefined && formData.ydate !== null) {
        submitData.ydate = getNumberValue(formData.ydate);
      }
      if (formData.xatach !== undefined && formData.xatach !== null) {
        submitData.xatach = getNumberValue(formData.xatach);
      }
      if (formData.yatach !== undefined && formData.yatach !== null) {
        submitData.yatach = getNumberValue(formData.yatach);
      }
      if (formData.xnumletter !== undefined && formData.xnumletter !== null) {
        submitData.xnumletter = getNumberValue(formData.xnumletter);
      }
      if (formData.ynumletter !== undefined && formData.ynumletter !== null) {
        submitData.ynumletter = getNumberValue(formData.ynumletter);
      }

      if (isEditMode && letterId) {
        // Add letter ID fields for edit mode - idletterstr is required
        submitData.idletter = 0;
        submitData.idletterstr = formData.idletterstr ? String(formData.idletterstr) : (letterId ? String(letterId) : "");
        
        // Override these to 0 in edit mode as required by API
        submitData.idUser = 0;
        submitData.idemza = 0;
        submitData.idsarbarg = 0;

        // Ensure all required string fields are present - use formData directly
        submitData.title = formData.title ? String(formData.title) : "";
        submitData.Sender = formData.sender ? String(formData.sender) : "";
        submitData.Receiver = formData.receiver ? String(formData.receiver) : "";
        submitData.Fullname = formData.fullname ? String(formData.fullname) : "";
        submitData.postName = formData.postName ? String(formData.postName) : "";
        submitData.Numletter = formData.numletter ? String(formData.numletter) : "";
        submitData.roonevesht = formData.roonevesht ? String(formData.roonevesht) : "";
        submitData.textletter = formData.textletter ? String(formData.textletter) : "";
        submitData.idletterstr = formData.idletterstr ? String(formData.idletterstr) : (letterId ? String(letterId) : "");
        // If useSignature is unchecked, set idemzastr to "0"
        submitData.idemzastr = (formData.useSignature === false) ? "0" : (formData.idemzastr ? String(formData.idemzastr) : "");
        // If useTemplate is unchecked, set idsarbargstr to "0"
        submitData.idsarbargstr = (formData.useTemplate === false) ? "0" : (formData.idsarbargstr ? String(formData.idsarbargstr) : "");

        // Log the payload for debugging
      
        // Edit existing letter
        await EditLetter(submitData);
        setMessage({ type: "success", text: "نامه با موفقیت ویرایش شد" });
        trackEvent("Letter", "Edit", "Letter edited successfully");
        trackFormSubmit("Edit Letter");
        // Navigate back to letter list after successful edit
        // setTimeout(() => {
        //   console.log("Navigating to /letter after edit");
        //   navigate("/letter", { replace: true });
        // }, 800);
      } else {
        // Add new letter
        await AddLetter(submitData);
        setMessage({ type: "success", text: "نامه با موفقیت ثبت شد" });
        trackEvent("Letter", "Add", "Letter added successfully");
        trackFormSubmit("Add Letter");
        // Navigate back to letter list after successful add
        // setTimeout(() => {
        //   console.log("Navigating to /letter after add");
        //   navigate("/letter", { replace: true });
        // }, 1000);
      }
    } catch (error) {
      console.error("Error submitting letter:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          (isEditMode ? "خطا در ویرایش نامه" : "خطا در ثبت نامه"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show loading state while checking letter head
  if (isCheckingLetterHead) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show message on mobile if no letter head exists
  if (showMobileMessage && !isEditMode) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh",
          p: 3,
          bgcolor: "#f5f5f5",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: "center",
            maxWidth: "500px",
            width: "100%",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
            برای افزودن نامه
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            لطفاً ابتدا تنظیمات اولیه را در نسخه دسکتاپ انجام دهید.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            پس از پیکربندی تنظیمات در دسکتاپ، می‌توانید در موبایل نیز نامه اضافه
            کنید.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: "center",
        p: 1,
        pb: 3,
      }}
    >
      <Box sx={{ width: "100%" }}>
        <AddLetterForm
          formData={formData}
          onChange={handleInputChange}
          onFileChange={handleFileChange}
          onNavigateToAdjustment={() => {
            navigate("/adjustment", {
              state: { formData, templateFile: formData.file || null },
            });
          }}
          onOpenEmzaDrawer={() => setEmzaDrawerOpen(true)}
          onSelectEmza={(id) => {
            if (id !== null && id !== undefined) {
              handleInputChange("idemzastr", id);
            }
            setEmzaDrawerOpen(false);
          }}
          onCloseEmzaDrawer={() => setEmzaDrawerOpen(false)}
          emzaDrawerOpen={emzaDrawerOpen}
          currentUserId={currentUserId}
        />
      </Box>

      {message.text && (
        <Alert
          severity={message.type}
          sx={{ width: "100%", maxWidth: "210mm" }}
        >
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          p: 1,
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            width: "100%",
            maxWidth: "210mm",
            borderRadius: "12px",
            padding: "10px",
            border: "1px solid white",
            textTransform: "none",
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <Typography variant="h5" sx={{ marginRight: "10px" }}>
              {isEditMode ? "ویرایش نامه" : "افزودن نامه"}
            </Typography>
          )}
        </Button>
      </Box>
    </Box>
  );
}
